import { useEffect, useState, type KeyboardEvent } from "react";

import type { LoginController } from "./useLoginController";

type Step = LoginController["step"];

type Props = {
  step: Step;
  loading: boolean;
  localError: string | null;
  resendTimer: number;
  handleSendOtp: () => void;
  handleVerifyOtp: () => void;
  handleActivateSession: () => void;
  handleChangeNumber: () => void;
  onPhoneKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onOtpKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
};

export type LoginViewStateResult = {
  isSendingOtp: boolean;
  isVerifyingOtp: boolean;
  isActivatingSession: boolean;
  canResend: boolean;
  handleSendOtpClick: () => void;
  handleLoginClick: () => void;
  handleContinueClick: () => void;
  handlePhoneKeyDownLocal: (e: KeyboardEvent<HTMLInputElement>) => void;
  handleOtpKeyDownLocal: (e: KeyboardEvent<HTMLInputElement>) => void;
  handleChangeNumberLocal: () => void;
};

export function useLoginViewState({
  step,
  loading,
  localError,
  resendTimer,
  handleSendOtp,
  handleVerifyOtp,
  handleActivateSession,
  handleChangeNumber,
  onPhoneKeyDown,
  onOtpKeyDown,
}: Props): LoginViewStateResult {
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isActivatingSession, setIsActivatingSession] = useState(false);

  const canResend = step === "otp" && resendTimer <= 0 && !loading;

  useEffect(() => {
    if (step === "otp") setIsSendingOtp(false);
    if (step === "enter") setIsVerifyingOtp(false);
    if (step !== "select") setIsActivatingSession(false);
  }, [step]);

  useEffect(() => {
    if (!loading && step === "enter") setIsSendingOtp(false);
    if (!loading && step === "otp") setIsVerifyingOtp(false);
    if (!loading && step === "select") setIsActivatingSession(false);
  }, [loading, step]);

  useEffect(() => {
    if (localError && step === "enter") setIsSendingOtp(false);
    if (localError && step === "otp") setIsVerifyingOtp(false);
    if (localError && step === "select") setIsActivatingSession(false);
  }, [localError, step]);

  const handleSendOtpClick = () => {
    if (isSendingOtp || loading || step === "otp") return;
    setIsSendingOtp(true);
    handleSendOtp();
  };

  const handleLoginClick = () => {
    if (isVerifyingOtp || loading || step !== "otp") return;
    setIsVerifyingOtp(true);
    handleVerifyOtp();
  };

  const handleContinueClick = () => {
    if (isActivatingSession || loading || step !== "select") return;
    setIsActivatingSession(true);
    handleActivateSession();
  };

  const handlePhoneKeyDownLocal = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (isSendingOtp || loading || step === "otp") { e.preventDefault(); return; }
      setIsSendingOtp(true);
    }
    onPhoneKeyDown(e);
  };

  const handleOtpKeyDownLocal = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (isVerifyingOtp || loading || step !== "otp") { e.preventDefault(); return; }
      setIsVerifyingOtp(true);
    }
    onOtpKeyDown(e);
  };

  const handleChangeNumberLocal = () => {
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
    setIsActivatingSession(false);
    handleChangeNumber();
  };

  return {
    isSendingOtp,
    isVerifyingOtp,
    isActivatingSession,
    canResend,
    handleSendOtpClick,
    handleLoginClick,
    handleContinueClick,
    handlePhoneKeyDownLocal,
    handleOtpKeyDownLocal,
    handleChangeNumberLocal,
  };
}
